import React, { useCallback, useEffect, useState } from 'react';
import PageHeading from '../components/PageHeading';
import { aiPropFirmAction, getAiAdminSettings, getAiAdminUsage, getAiPropFirms, resolveAiFeedback, saveAiAdminSettings } from '../services/aiAssistantApi';
import './AdminAiAssistant.css';

/*
 * Admin → RiskLo AI: on/off, model, monthly budget and per-tier limits, plus
 * this month's spend, who's using it, flagged answers and feedback.
 * Who can see RiskLo AI at all is set per tier in Users → Feature access.
 */

const TIERS = [
  { key: 'max', label: 'Max' },
  { key: 'sales', label: 'Sales' },
  { key: 'staff', label: 'Staff (Brand Admin)' },
  { key: 'support', label: 'Support' },
  { key: 'pro', label: 'Pro' },
  { key: 'basic', label: 'Basic' },
];
const money = (n, digits = 2) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

const STATUS_LABEL = { verified: 'Verified', changed: 'Change detected', error: 'Check failed', checking: 'Checking…', unconfirmed: 'Draft: waiting for first check' };

/** Prop firm rules RiskLo AI quotes: verified dates, automatic checks, pending changes. */
function PropFirmRules() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState('');
  const load = useCallback(() => getAiPropFirms().then(setData).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  // While any check is running, refresh every few seconds.
  useEffect(() => {
    if (!data?.firms?.some((f) => f.status === 'checking')) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data, load]);
  const act = async (key, action) => {
    setError('');
    try {
      await aiPropFirmAction(key, action);
      if (action === 'check') setData((d) => ({ ...d, firms: d.firms.map((f) => (key === 'all' || f.key === key ? { ...f, status: 'checking' } : f)) }));
      setTimeout(load, 800);
    } catch (e) { setError(e.message); }
  };
  if (!data) return <section className="aia-card"><h3>Prop firm rules</h3><p className="aia-muted">{error || 'Loading…'}</p></section>;
  const day = (d) => (d ? new Date(d).toLocaleDateString() : '—');
  return (
    <section className="aia-card">
      <div className="aia-row-head">
        <h3>Prop firm rules</h3>
        <button type="button" className="aia-btn" onClick={() => act('all', 'check')}>Check all now</button>
      </div>
      <p className="aia-muted">
        What RiskLo AI quotes, verified against each firm’s own pages. They’re re-checked automatically every {data.checkEveryDays} days; when a check finds a difference, it waits here for you to approve. Until then the AI keeps the verified rules and tells users the firm’s page may have changed. Each check costs about $0.10–0.30.
      </p>
      {error ? <p className="aia-error">{error}</p> : null}
      <table className="aia-table">
        <thead><tr><th>Firm</th><th>Status</th><th>Verified</th><th>Last check</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {data.firms.map((f) => (
            <React.Fragment key={f.key}>
              <tr>
                <td><button type="button" className="aia-link" onClick={() => setOpen(open === f.key ? '' : f.key)}>{f.firm}</button></td>
                <td><span className={`aia-status is-${f.status}`}>{STATUS_LABEL[f.status] || f.status}</span></td>
                <td>{day(f.verifiedAt)}<div className="aia-small">{f.verifiedBy}</div></td>
                <td>{day(f.checkedAt)}</td>
                <td><button type="button" className="aia-btn" disabled={f.status === 'checking'} onClick={() => act(f.key, 'check')}>Check now</button></td>
              </tr>
              {open === f.key || f.status === 'changed' ? (
                <tr className="aia-detail">
                  <td colSpan={5}>
                    {f.summary ? <p><b>Last check:</b> {f.summary}</p> : null}
                    {f.status === 'error' && f.error ? <p className="aia-error">{f.error}</p> : null}
                    <p className="aia-small">Sources: {(f.sources || []).map((s) => <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a>).reduce((a, el) => (a.length ? [...a, ' · ', el] : [el]), [])}</p>
                    {f.pendingRules ? (
                      <div className="aia-pending">
                        <b>Proposed update</b> (compare with the firm’s page before approving)
                        <div className="aia-compare">
                          <div><span>Current</span><pre>{JSON.stringify(f.rules.accounts, null, 1)}</pre></div>
                          <div><span>Proposed</span><pre>{JSON.stringify(f.pendingRules.accounts, null, 1)}</pre></div>
                        </div>
                        <div className="aia-actions">
                          <button type="button" className="aia-primary" onClick={() => act(f.key, 'approve')}>Approve update</button>
                          <button type="button" onClick={() => act(f.key, 'dismiss')}>Keep current rules</button>
                        </div>
                      </div>
                    ) : (
                      <pre className="aia-rules">{JSON.stringify(f.rules, null, 1)}</pre>
                    )}
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const KIND_LABEL = { idea: 'Idea', bug: 'Bug', wrong_answer: 'Wrong answer', praise: 'Praise', other: 'Other' };

/** Feedback sent from the panel's Feedback button. */
function GeneralFeedback({ items, onChange }) {
  const [showResolved, setShowResolved] = useState(false);
  const open = items.filter((g) => !g.resolved);
  const list = showResolved ? items : open;
  const toggle = async (g) => {
    try { await resolveAiFeedback(g.id, !g.resolved); onChange(g.id, !g.resolved); } catch (_e) { /* shown on reload */ }
  };
  return (
    <section className="aia-card">
      <h3>Feedback from users {open.length ? <span className="aia-pill">{open.length} open</span> : null}</h3>
      <label className="aia-muted aia-inline"><input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Show resolved</label>
      {list.length ? (
        <ul className="aia-list">
          {list.map((g) => (
            <li key={g.id} className={g.resolved ? 'is-resolved' : ''}>
              <div className="aia-list-head">
                <b>{KIND_LABEL[g.kind] || g.kind} · {g.email}{g.rating ? ` · ${'★'.repeat(g.rating)}${'☆'.repeat(5 - g.rating)}` : ''}</b>
                <span>{g.at}</span>
              </div>
              <p>{g.message}</p>
              <div className="aia-list-foot">
                <span className="aia-muted">{g.page ? `Page: ${g.page}` : ''}{g.conversation_id ? ` · Chat #${g.conversation_id} attached` : ''}</span>
                <button type="button" onClick={() => toggle(g)}>{g.resolved ? 'Reopen' : 'Mark resolved'}</button>
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="aia-muted">{items.length ? 'Nothing open.' : 'No feedback yet.'}</p>}
    </section>
  );
}

export default function AdminAiAssistant({ onNavigate }) {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [s, u] = await Promise.all([getAiAdminSettings(), getAiAdminUsage()]);
      setData(s);
      setDraft(s.settings);
      setUsage(u);
    } catch (e) {
      setError(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setNote('');
    setError('');
    try {
      const r = await saveAiAdminSettings({
        enabled: draft.enabled,
        model: draft.model,
        monthlyBudgetUsd: Number(draft.monthlyBudgetUsd),
        tierLimits: draft.tierLimits,
        deepEnabled: draft.deepEnabled,
        deepModel: draft.deepModel,
        deepCost: Number(draft.deepCost),
        webSearchEnabled: draft.webSearchEnabled,
        safetyCheck: draft.safetyCheck,
        privacy: draft.privacy,
      });
      setDraft(r.settings);
      setData((d) => ({ ...d, settings: r.settings }));
      setNote('Saved.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setLimit = (tier, field, value) => setDraft((d) => ({
    ...d,
    tierLimits: { ...d.tierLimits, [tier]: { ...d.tierLimits[tier], [field]: Math.max(0, Math.floor(Number(value) || 0)) } },
  }));

  if (error && !data) return (<><PageHeading title="RiskLo AI" description="AI assistant settings and usage." /><p className="aia-error">{error}</p></>);
  if (!data || !draft) return (<><PageHeading title="RiskLo AI" description="AI assistant settings and usage." /><p className="aia-muted">Loading…</p></>);

  const month = usage?.month || {};
  const budgetPct = month.budgetUsd > 0 ? Math.min(100, (month.costUsd / month.budgetUsd) * 100) : 0;
  const avg = month.avgCostPerAnswer || 0;
  const model = data.models.find((m) => m.id === draft.model);
  const maxDayMessages = Math.max(1, ...(usage?.byDay || []).map((d) => d.messages));
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.settings);

  return (
    <>
      <PageHeading title="RiskLo AI" description="AI assistant settings and usage. Admin only." />
      <div className="aia-page">
        {!data.apiKeyConfigured ? (
          <p className="aia-warn">No <code>ANTHROPIC_API_KEY</code> is set on the server, so RiskLo AI can’t answer yet. Add it to the server environment (Railway) and restart.</p>
        ) : null}
        <p className="aia-muted">
          Who sees RiskLo AI is set per tier in{' '}
          <button type="button" className="aia-link" onClick={() => onNavigate?.('admin-users')}>Users → Feature access</button>
          {' '}(“RiskLo AI (assistant)”). Today it’s off for every tier, so only Full Admins have it. Have the Terms and Privacy wording reviewed before opening it up.
        </p>

        <section className="aia-card">
          <h3>This month</h3>
          <div className="aia-kpis">
            <div><span>Spent</span><b>{money(month.costUsd)}</b><em>of {money(month.budgetUsd, 0)} budget</em></div>
            <div><span>Answers</span><b>{(month.messages || 0).toLocaleString()}</b></div>
            <div><span>Average per answer</span><b>{money(avg, 4)}</b></div>
            <div><span>Model</span><b>{model?.label || draft.model}</b></div>
            <div><span>Deep answers</span><b>{(usage?.deep?.answers || 0).toLocaleString()}</b><em>{money(usage?.deep?.costUsd || 0)}</em></div>
          </div>
          <div className={`aia-budget${budgetPct >= 80 ? ' is-hot' : ''}`} aria-label={`${budgetPct.toFixed(0)}% of budget used`}><i style={{ width: `${budgetPct}%` }} /></div>
          {budgetPct >= 100 ? <p className="aia-warn">Budget reached: RiskLo AI is paused for everyone except admins until the 1st, or until you raise the budget.</p> : null}
          {(usage?.byDay || []).length ? (
            <div className="aia-days">
              {usage.byDay.map((d) => (
                <div key={d.day} title={`${d.day}: ${d.messages} answers, ${money(d.cost, 4)}`}>
                  <i style={{ height: `${Math.max(4, (d.messages / maxDayMessages) * 100)}%` }} />
                </div>
              ))}
            </div>
          ) : <p className="aia-muted">No usage yet.</p>}
        </section>

        <section className="aia-card">
          <h3>Settings</h3>
          <div className="aia-form">
            <label className="aia-check"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> RiskLo AI is on</label>
            <label>Model
              <select value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })}>
                {data.models.map((m) => <option key={m.id} value={m.id}>{m.label} · ${m.input}/${m.output} per M tokens</option>)}
              </select>
            </label>
            <label>Monthly budget (all users, USD)
              <input type="number" min="0" step="1" value={draft.monthlyBudgetUsd} onChange={(e) => setDraft({ ...draft, monthlyBudgetUsd: e.target.value })} />
            </label>
          </div>
          <p className="aia-muted">A new model applies to new chats; existing chats keep theirs so their cache keeps working.</p>
          <div className="aia-form aia-form-gap">
            <label className="aia-check"><input type="checkbox" checked={draft.deepEnabled} onChange={(e) => setDraft({ ...draft, deepEnabled: e.target.checked })} /> Deep answers</label>
            <label>Deep answer model
              <select value={draft.deepModel} onChange={(e) => setDraft({ ...draft, deepModel: e.target.value })} disabled={!draft.deepEnabled}>
                {(data.deepModels || []).map((m) => <option key={m.id} value={m.id}>{m.label} · ${m.input}/${m.output} per M tokens</option>)}
              </select>
            </label>
            <label>A deep answer counts as
              <input type="number" min="1" max="20" value={draft.deepCost} onChange={(e) => setDraft({ ...draft, deepCost: e.target.value })} disabled={!draft.deepEnabled} />
            </label>
            <label className="aia-check"><input type="checkbox" checked={draft.webSearchEnabled} onChange={(e) => setDraft({ ...draft, webSearchEnabled: e.target.checked })} disabled={!draft.deepEnabled} /> Deep answers can search the news (≈ $0.01 per search, max 3 per answer)</label>
            <label className="aia-check"><input type="checkbox" checked={draft.safetyCheck} onChange={(e) => setDraft({ ...draft, safetyCheck: e.target.checked })} /> Second-opinion safety check on every answer (≈ $0.0003 each)</label>
          </div>
          <h4 className="aia-sub">Brand strategy privacy</h4>
          <div className="aia-form">
            <label>Minimum days before details are shared
              <input type="number" min="1" value={draft.privacy?.minDays ?? 20} onChange={(e) => setDraft({ ...draft, privacy: { ...draft.privacy, minDays: Number(e.target.value) } })} />
            </label>
            <label>Detailed lookups per strategy per user / day
              <input type="number" min="1" value={draft.privacy?.probeLimitPerStrategy ?? 8} onChange={(e) => setDraft({ ...draft, privacy: { ...draft.privacy, probeLimitPerStrategy: Number(e.target.value) } })} />
            </label>
            <label>All brand lookups per user / day
              <input type="number" min="1" value={draft.privacy?.probeLimitTotal ?? 25} onChange={(e) => setDraft({ ...draft, privacy: { ...draft.privacy, probeLimitTotal: Number(e.target.value) } })} />
            </label>
          </div>
          <p className="aia-muted">Past the limits, a user gets summary numbers only for the rest of the day and shows up under Probing alerts. Individual days and trades of brand strategies are never shared.</p>
          <table className="aia-table">
            <thead><tr><th>Tier</th><th>Per day</th><th>Per month</th><th>Worst case / user / month</th></tr></thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.key}>
                  <td>{t.label}</td>
                  <td><input type="number" min="0" value={draft.tierLimits[t.key]?.daily ?? 0} onChange={(e) => setLimit(t.key, 'daily', e.target.value)} aria-label={`${t.label} per day`} /></td>
                  <td><input type="number" min="0" value={draft.tierLimits[t.key]?.monthly ?? 0} onChange={(e) => setLimit(t.key, 'monthly', e.target.value)} aria-label={`${t.label} per month`} /></td>
                  <td>{avg ? money((draft.tierLimits[t.key]?.monthly || 0) * avg) : '—'}</td>
                </tr>
              ))}
              <tr><td>Admin</td><td colSpan={3} className="aia-muted">No limit (still counted)</td></tr>
            </tbody>
          </table>
          <p className="aia-muted">
            Run the test set before changing the model or prompt: <code>npm run ai:eval -- --email you@example.com --yes</code> (about $0.55).
            Limits only apply to tiers that have the feature. Each chat holds up to {data.limits.maxTurnsPerChat} questions of up to {data.limits.maxInputChars.toLocaleString()} characters; chats are deleted after {data.limits.keepDays} days.
          </p>
          <div className="aia-actions">
            <button type="button" className="aia-primary" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save'}</button>
            {dirty ? <button type="button" onClick={() => setDraft(data.settings)}>Discard</button> : null}
            {note ? <span className="aia-ok">{note}</span> : null}
            {error ? <span className="aia-error">{error}</span> : null}
          </div>
        </section>

        <PropFirmRules />

        <section className="aia-card">
          <h3>Top users this month</h3>
          {(usage?.topUsers || []).length ? (
            <table className="aia-table">
              <thead><tr><th>User</th><th>Answers</th><th>Cost</th></tr></thead>
              <tbody>{usage.topUsers.map((u) => <tr key={u.email}><td>{u.email}</td><td>{u.messages}</td><td>{money(u.cost, 4)}</td></tr>)}</tbody>
            </table>
          ) : <p className="aia-muted">Nobody yet.</p>}
        </section>

        <section className="aia-card">
          <h3>Probing alerts (last 7 days)</h3>
          <p className="aia-muted">People who looked up the same brand strategy many ways, the pattern of trying to reverse engineer it.</p>
          {(usage?.probeAlerts || []).length ? (
            <table className="aia-table">
              <thead><tr><th>User</th><th>Strategy</th><th>Lookups</th><th>Limited</th><th>Last</th></tr></thead>
              <tbody>{usage.probeAlerts.map((p) => <tr key={`${p.email}-${p.strategy_name}`}><td>{p.email}</td><td>{p.strategy_name}</td><td>{p.lookups}</td><td>{p.limited ? 'Yes' : 'No'}</td><td>{new Date(p.last_at).toLocaleString()}</td></tr>)}</tbody>
            </table>
          ) : <p className="aia-muted">None.</p>}
        </section>

        <section className="aia-card">
          <h3>Flagged answers</h3>
          <p className="aia-muted">Answers that looked like trading advice were replaced with a safe reply before the user saw the final text. Review them to tune the prompt.</p>
          {(usage?.flagged || []).length ? (
            <ul className="aia-list">
              {usage.flagged.map((f) => (
                <li key={f.id}>
                  <div className="aia-list-head"><b>{f.email}</b><span>{f.at}</span></div>
                  {f.question ? <p><em>Q:</em> {f.question}</p> : null}
                  <p className="aia-muted">{f.flag_reason}</p>
                </li>
              ))}
            </ul>
          ) : <p className="aia-muted">None.</p>}
        </section>

        <GeneralFeedback items={usage?.generalFeedback || []} onChange={(id, resolved) => setUsage((u) => ({ ...u, generalFeedback: u.generalFeedback.map((g) => (g.id === id ? { ...g, resolved } : g)) }))} />

        <section className="aia-card">
          <h3>Answer ratings</h3>
          {(usage?.feedback || []).length ? (
            <ul className="aia-list">
              {usage.feedback.map((f, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  <div className="aia-list-head"><b>{f.rating > 0 ? '👍' : '👎'} {f.email}</b><span>{f.at}</span></div>
                  {f.question ? <p><em>Q:</em> {f.question}</p> : null}
                  {f.answer ? <p className="aia-muted">{String(f.answer).slice(0, 400)}{String(f.answer).length > 400 ? '…' : ''}</p> : null}
                  {f.note ? <p>{f.note}</p> : null}
                </li>
              ))}
            </ul>
          ) : <p className="aia-muted">No feedback yet.</p>}
        </section>
      </div>
    </>
  );
}
